import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  Input,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexMarkers,
  ApexYAxis,
  ApexGrid,
  ApexTitleSubtitle,
  ApexLegend,
} from 'ng-apexcharts';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ChartResponses } from '../../../interfaces/reports/chart-responses';
import { BaseDashboardChartComponent } from '../base-dashboard-chart.component';
import { ChartCardComponent } from '../chart-card/chart-card.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import {TranslatePipe} from "@ngx-translate/core";
import { Subscription } from 'rxjs';

export type LineChartOptionsWithHistorical = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  tooltip: any; // ApexTooltip;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  colors: string[];
};

@Component({
  selector: 'co-line-chart-with-historical',
  imports: [NgApexchartsModule, ChartCardComponent, TableModule, SpinnerComponent, TranslatePipe],
  templateUrl: './line-chart-with-historical.component.html',
  styleUrl: './line-chart-with-historical.component.css',
})

export class LineChartWithHistoricalComponent extends BaseDashboardChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chart', { static: false }) chart?: ChartComponent;

  data = input.required<ChartResponses>();
  chartReady = input.required<boolean>();
  chartData = computed(() => this.data().data);
  chartTable = computed(() => this.data().table ?? []);
  @Input() xAxisLabel?: string;
  @Input() yAxisLabel?: string;
  allMonths = computed(() => {
    const monthSet = new Set<string>();
    this.data().data.forEach((group) => {
      group.data?.forEach((point) => {
        if (point.monthYear) monthSet.add(point.monthYear);
      });
    });
    return Array.from(monthSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  });
  categories = computed(() => this.allMonths());

  public series: any[] = [];
  public chartOptions: Partial<LineChartOptionsWithHistorical> = {};
  public chartVisible = true;
  public seriesDashArray: number[] = [];

  private langChangeSub?: Subscription;

  private readonly STYLE_MAP: Record<string, { color: string; dash: number; width?: number }> = {
    HistoricalData: { color: '#90A4AE', dash: 4 },
    CurrentData: { color: '#1976D2', dash: 0 },
    HistoricalMean: { color: '#FBC02D', dash: 8 },
    CurrentMean: { color: '#FFA000', dash: 8 },
    HistoricalUCL: { color: '#388E3C', dash: 6 },
    HistoricalLCL: { color: '#00796B', dash: 6 },
    CurrentUCL: { color: '#66BB6A', dash: 6 },
    CurrentLCL: { color: '#26A69A', dash: 6 },
    HistoricalTrend: { color: '#D48800', dash: 2 },
    CurrentTrend: { color: '#FF9800', dash: 2 },
    OOC: { color: '#D32F2F', dash: 10 },
    Excluded: { color: '#B71C1C', dash: 10 },
  };

  constructor(private translate: TranslateService, private cdr: ChangeDetectorRef) {
    super();

    this.langChangeSub = this.translate.onLangChange.subscribe((evt: LangChangeEvent) => {
      this.rebuildChartForLocale(evt.translations);
    });

    effect(() => {
      if (this.chartData()) {
        this.rebuildChartForLocale();
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  private rebuildChartForLocale(translations?: Record<string, string>) {
    const months = this.allMonths();
    this.seriesDashArray = [];

    const groups = this.data().data.filter((g) => g.data && g.data.length > 0);

    const newSeries = groups.map((group) => {
      const style = this.STYLE_MAP[group.type] || this.STYLE_MAP['CurrentData'];

      const pointMap = new Map<string, number>();
      group.data.forEach((p) => pointMap.set(p.monthYear, parseFloat(p.count)));

      const translationKey = `LEGEND.${group.name}`;
      const translatedName =
        (translations && translations[translationKey]) ||
        this.translate.instant(translationKey) ||
        group.name;

      this.seriesDashArray.push(style.dash);

      return {
        name: translatedName,
        data: months.map((m) => ({ x: m, y: pointMap.get(m) ?? null })),
        color: style.color,
      };
    });
    this.series = [...newSeries];

    const colors = this.series.map((s) => s.color);
    this.chartOptions = {
      series: this.series,
      chart: {
        height: 350,
        type: 'line',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, reset: true },
        },
      },
      colors,
      stroke: {
        width: 4,
        curve: 'straight',
        dashArray: this.seriesDashArray,
      },
      dataLabels: { enabled: false },
      legend: { show: true },
      markers: { size: 0, hover: { sizeOffset: 6 } },
      xaxis: {
        labels: { trim: false },
        title: { text: this.translate.instant(this.xAxisLabel ?? '') },
        categories: this.categories(),
      },
      yaxis: { show: true, title: { text: this.translate.instant(this.yAxisLabel ?? '') } },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val: number) => (val != null ? val.toFixed(2) : '') },
      },
      grid: { borderColor: '#f1f1f1' },
    };

    this.cdr.detectChanges();

    try {
      if (this.chart && typeof (this.chart as any).updateSeries === 'function') {
        (this.chart as any).updateSeries(this.series, true);
      }
    } catch (err) {
      console.warn('updateSeries errored', err);
    }

    try {
      if (this.chart && typeof (this.chart as any).updateOptions === 'function') {
        (this.chart as any).updateOptions(
          {
            colors,
            stroke: this.chartOptions.stroke,
            xaxis: this.chartOptions.xaxis,
            yaxis: this.chartOptions.yaxis,
            legend: this.chartOptions.legend,
          },
          true,
          true
        );
      }
    } catch (err) {
      console.warn('updateOptions errored', err);
    }
  }
  override onDropdownChange(): void {
    this.onFilterChange.emit(this.selectedFilters);
  }
}
